import { cx } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import Highlighter from "react-highlight-words";
import { InteractiveContainer } from "../../../InteractiveContainer";
import { Checkbox } from "../../../Checkbox";
import { ReactComponent as CheckIcon } from "../../assets/check.svg";

export interface ListBoxItemComponentProps {
  label: ReactNode;
  isSelected: boolean;
  isInteractive: boolean;
  height: number;
  isDisabled?: boolean;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  secondaryText?: ReactNode;
  highlightedText?: string;
  metaAttributes?: Record<string, string>;
  className?: string;
  onClick: () => void;
  buttonType?: "checkmark" | "checkbox";
}

export const ListBoxItemComponent: FC<ListBoxItemComponentProps> = ({
  label,
  isSelected,
  isInteractive,
  height,
  isDisabled = false,
  startSlot,
  endSlot,
  secondaryText,
  highlightedText,
  metaAttributes,
  className,
  onClick,
  buttonType = "checkmark",
}) => (
  <InteractiveContainer
    className={cx(
      "relative flex shrink-0 items-center gap-3 rounded-lg px-3 transition-colors",
      {
        "bg-primary-50": isSelected && buttonType === "checkmark",
        "hover:bg-grey-100":
          (!isSelected && isInteractive) ||
          (isSelected && buttonType === "checkbox"),
        "cursor-default": !isInteractive,
        "cursor-not-allowed": isDisabled,
      },
      className
    )}
    style={{ height }}
    onClick={isInteractive && !isDisabled ? onClick : undefined}
    {...metaAttributes}
  >
    {startSlot && <span className="shrink-0 leading-[0]">{startSlot}</span>}
    <div className="flex grow flex-col overflow-hidden">
      <span
        className={cx(
          "font-secondary truncate text-sm leading-[140%]",
          isDisabled && buttonType === "checkmark"
            ? "text-dark-500"
            : "text-dark"
        )}
      >
        {typeof label === "string" && highlightedText ? (
          <Highlighter
            autoEscape
            highlightClassName="text-primary font-medium"
            highlightTag="span"
            textToHighlight={label}
            searchWords={[highlightedText]}
          />
        ) : (
          label
        )}
      </span>
      {secondaryText && (
        <span className="text-grey-700 leading-2 truncate text-xs tracking-[0.12px]">
          {secondaryText}
        </span>
      )}
    </div>
    {endSlot ||
      (buttonType === "checkbox" ? (
        <Checkbox
          readOnly
          size="sm"
          isChecked={isSelected}
          isDisabled={isDisabled}
        />
      ) : (
        <CheckIcon
          className={cx(
            "shrink-0 transition-opacity",
            isSelected ? "opacity-100" : "opacity-0"
          )}
        />
      ))}
  </InteractiveContainer>
);
